// src/pages/public/Blogs/BlogDetail/BlogDetailPage.jsx
import React, { useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchBlogBySlug, likeBlog } from '../../../../redux/slices/blogsSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
import { formatDate } from '../../../../utils/formatDate.js'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import Newsletter from '../../../../components/common/Common/NewsLetter.jsx'
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'
import IconButton from '../../../../components/common/IconButton.jsx'
import { motion } from 'framer-motion'
import HomeBlogsPreview from '../../../../components/sections/HomeBlogsPreview.jsx'

import './BlogDetailPage.scss'

export default function BlogDetailPage() {
  const { slug } = useParams()
  const dispatch = useAppDispatch()
  const [likes, setLikes] = React.useState(0)
  const { currentBlog: blog, loading, error } = useAppSelector((state) => state.blogs)

  useEffect(() => {
    if (slug) {
      dispatch(fetchBlogBySlug(slug))
    }
  }, [dispatch, slug])

  useEffect(() => {
    if (blog?.likes) {
      setLikes(blog.likes)
    }
  }, [blog])

  const handleLike = () => {
    setLikes(prev => prev + 1)
    dispatch(likeBlog(blog.slug))
  }

  if (loading) {
    return (
      <>
        <SEO title="Loading Blog Post..." />
        <div className="space-y-12">
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !blog) {
    return (
      <>
        <SEO title="Blog Post Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Blog Post Not Found"
            message="The blog post you're looking for doesn't exist or has been removed."
            onRetry={() => slug && dispatch(fetchBlogBySlug(slug))}
          />
        </div>
      </>
    )
  }

  const hero = blog.hero || {}
  const about = blog.about || {}
  const postSection = blog.postSection || {}
  const insightSection = blog.insightSection || {}
  const relatedBlogs = Array.isArray(blog.relatedBlogs) ? blog.relatedBlogs : []

  const heroImage = hero.imageUrl || blog.featuredImage

  return (
    <>
      <SEO
        title={blog.metaTitle || blog.title}
        description={blog.metaDescription || blog.excerpt || blog.about?.para}
        image={heroImage}
        type="article"
        publishedTime={blog.publishedAt}
        modifiedTime={blog.updatedAt}
        author={blog.author?.name}
        tags={blog.tags}
      />

      <article className="blog-detail-page">
        {/* HERO – v1 card-hero-section style, v2 data */}
        {hero.isVisible !== false && (
          <section className="card-hero-section">
            <div className="card-hero-container">
              <motion.div
                className="card-hero"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                {/* Background Image */}
                <div className="card-background">
                  {heroImage ? (
                    <LazyImage
                      src={heroImage}
                      alt={hero.heading || blog.title}
                      className="card-bg-image"
                      placeholder={<div className="card-bg-placeholder" />}
                    />
                  ) : (
                    <div className="card-bg-placeholder" />
                  )}
                  <div className="card-overlay" />
                </div>

                {/* Content */}
                <div className="card-content">
                  <motion.h1
                    className="card-heading"
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    {(hero.heading || blog.title || '').toUpperCase()}
                  </motion.h1>

                  {hero.type && (
                    <motion.div
                      className="card-capsule"
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: 0.5 }}
                    >
                      {hero.type}
                    </motion.div>
                  )}
                </div>
              </motion.div>
            </div>
          </section>
        )}



        {/* ABOUT + POST SECTION – v1 InsightDetails style, v2 blog data */}
        {(about.isVisible !== false && about.para) ||
          (postSection.isVisible !== false &&
            (postSection.Heading ||
              (Array.isArray(postSection.cards) && postSection.cards.length > 0) ||
              postSection.conclusion)) ? (
          <section className="blog-content-section">
            <div className="blog-content-container">
              {/* Green block (about) */}
              {about.isVisible !== false && about.para && (
                <motion.div
                  className="green-block"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <p className="block-text">{about.para}</p>
                </motion.div>
              )}

              {/* Post heading as big paragraph */}
              {postSection.isVisible !== false && postSection.Heading && (
                <motion.p
                  className="insight-paragraph"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {postSection.Heading}
                </motion.p>
              )}

              {/* Cards -> vertical lists (title + key/value list) */}
              {postSection.isVisible !== false &&
                Array.isArray(postSection.cards) &&
                postSection.cards.length > 0 && (
                  <motion.div
                    className="vertical-lists"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    {postSection.cards.map((card, index) => (
                      <div key={index} className="list-item">
                        {card.title && (
                          <h3 className="list-label">{card.title}</h3>
                        )}
                        {Array.isArray(card.list) && card.list.length > 0 && (
                          <div className="list-points">
                            {card.list.map((item, itemIndex) => (
                              <div key={itemIndex} className="point">
                                {item.key && (
                                  <div className="point-title">
                                    {item.key} :
                                  </div>
                                )}
                                {item.value && (
                                  <div className="point-text">
                                    {item.value}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}

              {/* Conclusion */}
              {postSection.isVisible !== false && postSection.conclusion && (
                <motion.p
                  className="insight-paragraph final-paragraph"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                >
                  {postSection.conclusion}
                </motion.p>
              )}
            </div>
          </section>
        ) : null}

        {/* AUTHOR / META / LIKE ROW – same data & behaviour, dark UI */}
        <section className="blog-meta-section">
          <div className="blog-meta-container">
            <div className="blog-meta-author">
              {blog.author?.imageUrl && (
                <LazyImage
                  src={blog.author.imageUrl}
                  alt={blog.author.name}
                  className="author-avatar"
                  placeholder={<div className="author-avatar placeholder" />}
                />
              )}
              {/* <div>
                <p className="author-name">{blog.author?.name || 'Unknown Author'}</p>
                {blog.author?.role && (
                  <p className="author-role">{blog.author.role}</p>
                )}
              </div> */}
              <div className="meta-item">
                <span className="meta-text">
                  {formatDate(blog.publishedAt || blog.createdAt)}
                </span>
              </div>
            </div>

            <div className="blog-meta-info">


              {blog.readTime && (
                <div className="meta-item">
                  <span className="meta-icon">⏱️</span>
                  <span className="meta-text">{blog.readTime}</span>
                </div>
              )}

              <div className="meta-item like-item">
                <IconButton
                  icon="❤️"
                  variant="ghost"
                  size="small"
                  onClick={handleLike}
                  className="blog-like-button"
                />
                <span className="meta-text">{likes || 0}</span>
              </div>
            </div>
          </div>
        </section>

        <Newsletter />

        {/* INSIGHT SECTION – simple dark block */}
        {insightSection.isVisible !== false &&
          (insightSection.Heading) && (
            <section className="blog-insight-section">
              <div className="blog-insight-container">

                <HomeBlogsPreview sectionData={insightSection} />
              </div>
            </section>
          )}

        {/* TAGS – dark pills */}
        {blog.tags && blog.tags.length > 0 && (
          <section className="blog-tags-section">
            <div className="blog-tags-container">
              <h3 className="tags-heading">Tags</h3>
              <div className="tags-list">
                {blog.tags.map((tag, index) => (
                  <Link
                    key={index}
                    to={`/blogs?tag=${tag}`}
                    className="tag-pill"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* AUTHOR BIO – dark card */}
        {blog.author?.bio && (
          <section className="blog-author-section">
            <div className="blog-author-container">
              <div className="author-card">
                {blog.author.imageUrl && (
                  <LazyImage
                    src={blog.author.imageUrl}
                    alt={blog.author.name}
                    className="author-card-avatar"
                    placeholder={
                      <div className="author-card-avatar placeholder" />
                    }
                  />
                )}
                <div className="author-card-content">
                  <h3 className="author-card-title">
                    About {blog.author.name}
                  </h3>
                  <p className="author-card-bio">{blog.author.bio}</p>
                </div>
              </div>
            </div>
          </section>
        )}


        {/* RELATED BLOGS – dark grid */}
        {relatedBlogs.length > 0 && (
          <section className="related-blogs-section">
            <div className="related-blogs-container">
              <h2 className="related-heading">Related Articles</h2>
              <div className="related-grid">
                {relatedBlogs.slice(0, 3).map((relatedBlog) => (
                  <Link
                    key={relatedBlog._id}
                    to={`/blog/${relatedBlog.slug}`}
                    className="related-blog-card"
                  >
                    <h3 className="related-blog-title">
                      {relatedBlog.title}
                    </h3>
                    {relatedBlog.excerpt && (
                      <p className="related-blog-excerpt">
                        {relatedBlog.excerpt}
                      </p>
                    )}
                    <div className="related-blog-meta">
                      <span>
                        {formatDate(
                          relatedBlog.publishedAt || relatedBlog.createdAt
                        )}
                      </span>
                      {relatedBlog.readTime && (
                        <>
                          <span className="dot">•</span>
                          <span>{relatedBlog.readTime}</span>
                        </>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  )
}
